import type { TelFilter } from '../est/filters';
import type { RootScope } from '../types';
import type { UiNotificationService } from '../ui/notification';

import { opportunityApi } from '../services/api';
import { QueryBuilder } from '../unit.search.panel/queryBuilder';

interface LsLead {
  id?: string;
  name?: string;
  phone?: string | null;
  email?: string | null;
  active_opportunity_id?: number | null;
  integration?: {
    deal?: {
      name: string | null;
    };
  };
}

export class WorkingWithCtrl {
  showWorkingWith = false;
  lastLeadTooltip = '';
  textNoName = '';
  lead: LsLead | null = null;

  static $inject = [
    '$scope',
    '$window',
    '$timeout',
    '$translate',
    'telFilter',
    'localStorageService',
    '$rootScope',
    '$notification',
    '$q',
    '$mdDialog',
  ];

  constructor(
    $scope: angular.IScope,
    private $window: angular.IWindowService,
    private $timeout: angular.ITimeoutService,
    private $translate: angular.translate.ITranslateService,
    private telFilter: TelFilter,
    private localStorageService: angular.local.storage.ILocalStorageService,
    private $rootScope: RootScope,
    private $notification: UiNotificationService,
    private $q: angular.IQService,
    private $mdDialog: angular.material.IDialogService,
  ) {
    this.textNoName = this.$translate.instant('unit.popup.noname');

    $rootScope.$on('$translateChangeSuccess', () => {
      this.textNoName = this.$translate.instant('unit.popup.noname');
    });

    $scope.$on('new.ls.lead', () => {
      this.init();

      $scope.$apply();
    });

    this.init();
  }

  init() {
    let lsLead: LsLead | null = null;

    if (this.localStorageService.get('lead')) {
      lsLead = this.localStorageService.get('lead');
    }

    if (lsLead) {
      let lastLeadTooltip = '';

      if (typeof lsLead.name === 'undefined') {
        lastLeadTooltip = this.textNoName;
      } else {
        lastLeadTooltip = lsLead.name;
      }

      if (this.$rootScope.embeddedIntegrationName) {
        if (
          typeof lsLead.integration !== 'undefined' &&
          typeof lsLead.integration.deal !== 'undefined' &&
          lsLead.integration.deal.name
        ) {
          lastLeadTooltip += ` [${lsLead.integration.deal.name}]`;
        } else {
          lastLeadTooltip += ` [${this.textNoName}]`;
        }
      } else {
        if (typeof lsLead.phone !== 'undefined' && lsLead.phone !== null) {
          lastLeadTooltip += ` +${this.telFilter(lsLead.phone)}`;
        }

        if (typeof lsLead.email !== 'undefined' && lsLead.email !== null) {
          lastLeadTooltip += ` ${lsLead.email}`;
        }
      }

      this.lastLeadTooltip = lastLeadTooltip.trim();
      this.showWorkingWith = true;
      this.$rootScope.workingWithActive = true;
      this.lead = lsLead;
      this.$rootScope.leadHasActiveOpportunity =
        !!this.lead.active_opportunity_id;
    }
  }

  async openFiltersModal() {
    if (!this.lead?.id) {
      return;
    }

    const objects = this.$rootScope.objectsNaviOriginal ?? [];

    const saved = await opportunityApi
      .getSelectedUnitSegments(this.lead.id)
      .catch(() => null);

    // Convert [{ house_id, section_ids[] }] → { [sectionId]: true }
    const initialSelected: Record<number, boolean> = {};

    if (saved) {
      saved.forEach(({ section_ids }) => {
        section_ids.forEach((id) => {
          initialSelected[id] = true;
        });
      });
    }

    this.$mdDialog
      .show({
        clickOutsideToClose: true,
        template: require('./working.with.save-filters-modal.html'),
        locals: { objects, initialSelected },
        controller: [
          '$scope',
          '$mdDialog',
          'objects',
          'initialSelected',
          (
            $scope: angular.IScope & {
              objects: any[];
              selected: Record<number, boolean>;
              isHouseChecked(house: any): boolean;
              isHouseIndeterminate(house: any): boolean;
              toggleHouse(house: any): void;
              closeDialog(): void;
              save(): void;
            },
            $mdDialog: angular.material.IDialogService,
            objects: any[],
            initialSelected: Record<number, boolean>,
          ) => {
            $scope.objects = objects;
            $scope.selected = { ...initialSelected };

            $scope.isHouseChecked = (house: any) =>
              !!house.sectionList?.length &&
              house.sectionList.every((s: any) => !!$scope.selected[s.id]);

            $scope.isHouseIndeterminate = (house: any) => {
              if (!house.sectionList?.length) {
                return false;
              }

              const n = house.sectionList.filter(
                (s: any) => !!$scope.selected[s.id],
              ).length;

              return n > 0 && n < house.sectionList.length;
            };

            $scope.toggleHouse = (house: any) => {
              const allChecked = $scope.isHouseChecked(house);
              house.sectionList?.forEach((s: any) => {
                $scope.selected[s.id] = !allChecked;
              });
            };

            $scope.closeDialog = () => $mdDialog.hide();

            $scope.save = () => {
              const segments: { house_id: number; section_ids: number[] }[] =
                [];
              objects.forEach((obj: any) => {
                obj.houses?.forEach((house: any) => {
                  const sectionIds = (house.sectionList ?? [])
                    .filter((s: any) => !!$scope.selected[s.id])
                    .map((s: any) => s.id);

                  if (sectionIds.length) {
                    segments.push({
                      house_id: house.id,
                      section_ids: sectionIds,
                    });
                  }
                });
              });
              $mdDialog.hide(segments.length ? segments : null);
            };
          },
        ],
      })
      .then(
        (segments: { house_id: number; section_ids: number[] }[] | null) => {
          if (segments !== undefined) {
            this.addFiltersToOpportunityFavorites(segments ?? undefined);
          }
        },
      );
  }

  private async addFiltersToOpportunityFavorites(
    selectedUnitSegments?: { house_id: number; section_ids: number[] }[],
  ) {
    if (!this.lead?.id) {
      return;
    }

    try {
      await opportunityApi.saveSelectedSegmentFilters({
        leadId: this.lead.id,
        filters: QueryBuilder.getInstance()?.toFilters() ?? [],
        selectedUnitSegments,
      });

      this.$notification.show({
        type: 'success',
        text: this.$translate.instant('working.with.filters.saved'),
      });
    } catch {
      this.$notification.show({
        type: 'error',
        text: this.$translate.instant('working.with.filters.save.error'),
      });
    }
  }

  async copyOpportunityFavoritesLink() {
    if (!this.lead?.id) {
      return;
    }

    try {
      const url = await opportunityApi.getSelectedSegmentLink(this.lead.id);

      await navigator.clipboard.writeText(url);

      this.$notification.show({
        type: 'success',
        text: this.$translate.instant('working.with.link.copied'),
      });
    } catch {
      this.$notification.show({
        type: 'error',
        text: this.$translate.instant('working.with.link.copy.error'),
      });
    }
  }

  finishWorkingWith() {
    this.localStorageService.remove('lead');
    this.showWorkingWith = false;
    this.$rootScope.workingWithActive = false;
    this.$rootScope.leadHasActiveOpportunity = false;

    this.$timeout(() => {
      if (this.$window.location.pathname.startsWith('/appeals')) {
        // if we are on appeals page, just stay where we are
        return;
      }

      this.$window.location.href = '/leads';
    }, 300);
  }
}
